<script lang="ts" setup>
	import type { ChartData } from 'chart.js'
	import type { MycoverOverviewCard } from './overview-card.vue'
	import type { filterCollectionSchema } from '@/components/Forms/filter.vue'

	defineProps<{
		overview: Record<string, MycoverOverviewCard>
		filterList: filterCollectionSchema[]
		filterBy: string | number
		chartDetails: ChartData<'bar'>
		loading?: boolean
	}>()
	defineEmits<{ (event: 'update:filterBy', param: string): void }>()
</script>

<template>
	<div>
		<div class="mb-14 flex items-center justify-between gap-x-3 overflow-x-auto">
			<loader-skeleton v-if="loading" :height="80" />
			<template v-for="(item, k) in overview" v-else :key="k">
				<my-cover-overview-card
					v-if="!item.disabled"
					:value="item.value"
					:title="k"
					:title-class="item.titleClass"
					:value-class="item.valueClass"
					:list-class="[item?.listClass || '', 'mt-2.5']"
					:list-style="item?.listStyle"
					:no-list-point="item.noListPoint"
					:style="item?.style"
					class="flex w-full max-w-[180px] flex-none"
					:class="item.wrapperClass"
				/>
			</template>
		</div>

		<div class="flex flex-wrap items-center justify-between">
			<div class="space-y-2 tracking-wide">
				<slot name="label" />
			</div>

			<forms-filter
				:filter-by="filterBy"
				:filter-collection="filterList"
				class="!rounded border px-2 py-[6px]"
				:use-default-class="false"
				@update:filter-by="e => $emit('update:filterBy', e)"
			>
				<template #active-text>
					<div class="flex items-center gap-x-2">
						<lazy-tools-icon
							icon="note"
							dir="documents"
							:height="16"
							:width="16"
							path-class="stroke-gray-950 fill-transparent"
						/>
						<span>{{ filterBy }}</span>
					</div>
				</template>
			</forms-filter>
		</div>

		<div class="overflow-x-auto">
			<charts-bar-loader v-if="loading" />
			<charts-bar v-else :data="chartDetails" class="mt-12" />
			<ul class="mt-[32px] flex flex-col gap-y-2">
				<li
					v-for="(value, index) in chartDetails?.datasets"
					:key="value?.label || index"
					class="inline-flex items-center gap-2 text-xs font-medium tracking-wide text-gray-600"
				>
					<span class="h-2 w-2 rounded-full" :style="{ background: value.backgroundColor as string }" />
					<span>{{ value.label }}</span>
				</li>
			</ul>
		</div>
	</div>
</template>
